import {
  Box,
  Button,
  type ButtonProps,
  Icon,
  IconButton,
  Menu,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { ChevronDown, X } from 'lucide-react'
import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

export interface FilterMenuBaseProps {
  /**
   * The label to display on the menu button
   */
  buttonLabel: string

  /**
   * The header title to show in the menu
   * If not provided, defaults to "Filter by {buttonLabel}"
   */
  headerTitle?: string

  /**
   * Custom class name for additional styling
   */
  className?: string

  /**
   * Additional styles for the component
   */
  style?: CSSProperties

  /**
   * Size for the button (uses Chakra UI's ButtonProps['size'])
   */
  size?: ButtonProps['size']

  /**
   * Width of the menu list in pixels
   * This ensures the menu has a consistent width regardless of content
   * @default 300
   */
  menuWidth?: number

  /**
   * Currently selected value - affects button styling
   */
  hasSelectedValue?: boolean

  /**
   * Additional props to be spread to the Menu component
   */
  menuProps?: Record<string, unknown>

  /**
   * Children to render inside the menu list (after the header)
   * Can be ReactNode or a function that receives a close function
   */
  children?: ReactNode | ((props: { closeMenu: () => void }) => ReactNode)

  /**
   * Callback function triggered when the menu is opened
   */
  onMenuOpen?: () => void

  /**
   * Callback function triggered when the menu is closed
   */
  onMenuClose?: () => void

  /**
   * Whether to highlight the button when a value is selected
   * This changes the button variant and color scheme
   * @default false
   */
  highlightWhenSelected?: boolean
}

/**
 * Base component for filter menu UI that handles the shared menu structure
 * and behaviors between different filter menu variants.
 */
export function FilterMenuBase({
  buttonLabel,
  headerTitle,
  className = '',
  style = {},
  size = 'sm',
  menuWidth = 300,
  hasSelectedValue = false,
  highlightWhenSelected = false,
  menuProps = {},
  children,
  onMenuOpen,
  onMenuClose,
}: FilterMenuBaseProps) {
  const { t } = useTranslation()
  const translatedHeaderTitle =
    headerTitle || t('components.filterMenu.filterBy', { label: buttonLabel })

  // Menu state and refs
  const { open, onOpen, onClose } = useDisclosure()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const [calculatedMenuWidth, setCalculatedMenuWidth] = useState<
    number | undefined
  >(undefined)

  // Capture the menu width when it opens
  useEffect(() => {
    if (open && menuButtonRef.current && !menuWidth) {
      // Only calculate width if no width was provided as a prop
      setCalculatedMenuWidth(menuButtonRef.current.offsetWidth)
    }
  }, [open, menuWidth])

  // Call callbacks when the menu opens or closes
  useEffect(() => {
    if (open && onMenuOpen) {
      onMenuOpen()
    } else if (!open && onMenuClose) {
      onMenuClose()
    }
  }, [open, onMenuOpen, onMenuClose])

  // Handle open/close with custom callbacks
  const handleOpen = () => {
    onOpen()
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Menu.Root
      closeOnSelect={false}
      lazyMount={false}
      open={open}
      onOpenChange={(details) => {
        if (details.open) {
          handleOpen()
        } else {
          handleClose()
        }
      }}
      {...menuProps}
    >
      <Menu.Trigger asChild>
        <Button
          ref={menuButtonRef}
          variant={
            highlightWhenSelected && hasSelectedValue ? 'solid' : 'ghost'
          }
          colorPalette={
            highlightWhenSelected && hasSelectedValue ? 'blue' : undefined
          }
          size={size}
          className={className}
          pr={2}
          style={style}
        >
          {buttonLabel}
          <Icon as={ChevronDown} />
        </Button>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            width={`${menuWidth || calculatedMenuWidth || 300}px`}
            minWidth={`${menuWidth || calculatedMenuWidth || 300}px`}
            overflowX="hidden"
            py={0}
          >
            {/* Fixed header section */}
            <Box
              pl={3}
              pr={1}
              py={1}
              fontWeight="bold"
              borderBottom="1px solid"
              borderColor="gray.200"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Text fontSize={size}>{translatedHeaderTitle}</Text>
              <IconButton
                variant="ghost"
                aria-label={t('components.filterMenu.closeFilter') as string}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onClose()
                }}
                _hover={{ bg: 'transparent', opacity: 0.8 }}
              >
                <Icon as={X} />
              </IconButton>
            </Box>

            {/* Content section */}
            {typeof children === 'function'
              ? children({ closeMenu: handleClose })
              : children}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}

/**
 * Shared component for input sections in filter menus
 */
export function FilterMenuSection({
  children,
  onClick,
}: {
  children: ReactNode
  onClick?: (e: MouseEvent) => void
}) {
  return (
    <Box
      px={3}
      py={2}
      borderBottom="1px solid"
      borderColor="gray.200"
      onClick={(e) => {
        // Prevent click from closing the menu
        e.stopPropagation()
        if (onClick) {
          onClick(e)
        }
      }}
    >
      {children}
    </Box>
  )
}
